/**
 * remarkTrimAutolinkTrailingJunk — fixes GFM autolink-literal links that
 * swallow trailing junk characters into the URL.
 *
 * The problem:
 *   remark-gfm's autolink-literal extension turns bare URLs in prose into
 *   links, and it trims common trailing punctuation (`.`, `,`, `)`, ...)
 *   per the GFM spec. But backticks and quotes are NOT in the spec's trim
 *   list, so text like:
 *
 *       Ya https://cellcog.ai/app/abc/admin.html` check
 *
 *   (an unbalanced inline-code wrap, which agents produce regularly)
 *   yields a link whose href literally ends with a backtick — which the
 *   browser percent-encodes to %60 on navigation, 404ing the app URL.
 *
 * The fix:
 *   After parsing, walk every `link` node that is an autolink-literal
 *   (identified by its single text child being exactly equal to its URL —
 *   explicit `[text](url)` links are never touched) and move any trailing
 *   backticks / quotes out of the link: the URL and visible link text are
 *   trimmed in sync, and the trimmed characters are re-inserted as a plain
 *   text sibling right after the link so no user content is dropped.
 *
 * Properly balanced `inline code` wrapping is unaffected — micromark
 * resolves matched backticks into an inlineCode node before autolinking,
 * so no link node exists in that case.
 */
import { visit } from 'unist-util-visit';

const TRAILING_JUNK_RE = /[`'"]+$/;

const remarkTrimAutolinkTrailingJunk = () => {
  return (tree) => {
    visit(tree, 'link', (node, index, parent) => {
      if (!parent || typeof index !== 'number') return;
      if (!Array.isArray(node.children) || node.children.length !== 1) return;

      const child = node.children[0];
      if (!child || child.type !== 'text' || typeof child.value !== 'string') return;

      // Only autolink-literals: the visible text IS the URL. Explicit
      // [text](url) links are left alone — the author chose that URL.
      if (typeof node.url !== 'string' || child.value !== node.url) return;

      const match = node.url.match(TRAILING_JUNK_RE);
      if (!match) return;

      const junk = match[0];
      const clean = node.url.slice(0, node.url.length - junk.length);
      if (!clean) return;

      node.url = clean;
      child.value = clean;

      // Preserve the trimmed characters as plain text after the link so
      // the rendered prose still reads exactly as the author wrote it.
      parent.children.splice(index + 1, 0, { type: 'text', value: junk });
      return index + 2;
    });
  };
};

export default remarkTrimAutolinkTrailingJunk;
